"use client";

import { motion, useReducedMotion } from "motion/react";
import type { Variants } from "motion/react";
import { ShieldCheck, Eye, Users, Scale } from "lucide-react";

// ── Data ────────────────────────────────────────────────────────────────────

const VALUES = [
  {
    icon: ShieldCheck,
    title: "Trust",
    body: "Every gram is real and stored in fully insured vaults.",
  },
  {
    icon: Eye,
    title: "Transparency",
    body: "Live pricing and on chain records mean you always see exactly what you own.",
  },
  {
    icon: Users,
    title: "Accessibility",
    body: "Start small and grow at your own pace. Real gold ownership open to everyone.",
  },
  {
    icon: Scale,
    title: "Integrity",
    body: "Shariah compliant and interest free, structured around ethical principles.",
  },
];

// ── Motion ──────────────────────────────────────────────────────────────────

const container: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

function fadeUp(reduce: boolean): Variants {
  return {
    hidden: { opacity: 0, y: reduce ? 0 : 22 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, ease: [0.2, 0, 0, 1] },
    },
  };
}

// ── Section ─────────────────────────────────────────────────────────────────

export function AboutValues() {
  const reduce = useReducedMotion() ?? false;

  return (
    <motion.section
      className="border-t border-[#e3ecfb] bg-[#f7faff] pb-16 pt-16 sm:pb-20 sm:pt-20 lg:pb-24 lg:pt-24"
      aria-labelledby="about-values-heading"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.15 }}
      variants={container}
    >
      <div className="mx-auto max-w-[1200px] px-6 sm:px-10 lg:px-8">
        {/* Header */}
        <motion.div
          variants={fadeUp(reduce)}
          className="mb-12 max-w-[640px]"
        >
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-primary-500">
            What we stand for
          </p>
          <h2
            id="about-values-heading"
            className="text-[clamp(1.75rem,3.5vw,2.5rem)] font-semibold leading-[1.15] tracking-[-0.015em] text-primary-900"
          >
            Values that guide every decision.
          </h2>
        </motion.div>

        {/* Cards */}
        <motion.div
          variants={container}
          className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4"
        >
          {VALUES.map(({ icon: Icon, title, body }) => (
            <motion.div
              key={title}
              variants={fadeUp(reduce)}
              className="group flex flex-col rounded-card border border-[#e3ecfb] bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-primary-200 hover:shadow-[0_18px_40px_-24px_rgba(10,45,110,0.28)]"
            >
              <span
                className="flex h-11 w-11 items-center justify-center rounded-card-sm bg-primary-50 text-primary-600 transition-colors duration-300 group-hover:bg-primary-100"
                aria-hidden="true"
              >
                <Icon className="h-5 w-5" strokeWidth={2} />
              </span>
              <h3 className="mt-5 text-lg font-bold tracking-[-0.01em] text-text-heading">
                {title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-text-body">
                {body}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
}
